import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Practical site for "Who Ethical Hackers Are".
 *
 * The lesson's claim is that permission is the whole difference. This
 * is that claim with the paperwork attached, because "get permission"
 * sounds simple right up until somebody senior tells you verbally that
 * it is fine.
 *
 * A real-shaped authorisation letter, then eight decisions against it.
 * Three of them are the traps that catch people in practice: a system
 * that is obviously related but not listed, an instruction from someone
 * important given the wrong way, and a discovery that stops being a
 * test and starts being somebody's private life.
 *
 * The letter stays on screen throughout. Reading it is the exercise.
 */

const STORE_KEY = 'cybercub.lab.scope';

const LETTER = {
  client: 'Marbury Tutors Ltd',
  signed: 'R. Okonkwo, Operations Director',
  dated: '2 September 2026',
  window: 'Mon 14 – Fri 18 September 2026, 09:00–17:00 only',
  inScope: [
    'www.marburytutors.co.uk',
    'booking.marburytutors.co.uk',
    'The staff wifi at the Marbury office (guest network only)',
  ],
  outOfScope: [
    'Anything not named above, including other subdomains',
    'The payments provider (a separate company)',
    'Staff personal devices and personal accounts',
    'Any test that could take a system offline',
  ],
  rules: [
    'Prove a weakness exists. Do not go further into the system than that.',
    'Real customer data must not be copied, downloaded or read beyond proving access.',
    'Stop immediately and telephone the contact below if anything unexpected happens.',
    'Findings belong to Marbury Tutors until they say otherwise.',
  ],
  contact: 'R. Okonkwo — 07700 900 118',
};

/* ------------------------------------------------------------------ */

const CHOICES = [
  { id: 'go', label: 'In scope — carry on', icon: '✅' },
  { id: 'no', label: 'Out of scope — do not', icon: '🛑' },
  { id: 'ask', label: 'Stop and telephone the contact', icon: '☎️' },
];

const CASES = [
  {
    id: 'c1',
    icon: '🔎',
    text: 'You start by reading everything public about the company — the website, job adverts, the team page.',
    answer: 'go',
    why: 'Reading what a company has published is in scope and is where every real test starts. Nothing you do here touches their systems at all.',
  },
  {
    id: 'c2',
    icon: '🌐',
    text: 'You notice dev.marburytutors.co.uk. It is clearly theirs, and it looks much softer than the live site.',
    answer: 'no',
    why: 'Obviously theirs, and not on the list. The letter says other subdomains are out — and "it was obviously in the spirit of it" has never once worked as a defence.',
    trap: true,
  },
  {
    id: 'c3',
    icon: '📞',
    text: 'You mention dev to the Operations Director on the phone. "Oh, go ahead, that one’s fine."',
    answer: 'ask',
    why: 'He may well be able to authorise it — but not like that. Ask him to add it to the scope in writing. The point is not that you distrust him; it is that a phone call protects nobody, including him.',
    trap: true,
  },
  {
    id: 'c4',
    icon: '🚪',
    text: 'The booking site lets you reach another customer’s booking by changing a number in the address bar.',
    answer: 'go',
    why: 'booking is in scope and you have just proved a real weakness. Screenshot the one record that proves it and stop there — the rules say prove it, not explore it.',
  },
  {
    id: 'c5',
    icon: '🗂️',
    text: 'The same weakness would let you pull the details of all 4,000 customers into a file.',
    answer: 'no',
    why: 'You have already proved it. Downloading the data adds nothing to the finding and turns a test into a breach — of their customers’ privacy and very probably of the law.',
  },
  {
    id: 'c6',
    icon: '💳',
    text: 'The booking flow hands off to a payment company. You spot something odd on the payment company’s own site.',
    answer: 'ask',
    why: 'Not yours to test — different company, explicitly out of scope. But it is worth telling Marbury so they can raise it with their supplier, who can authorise it properly.',
  },
  {
    id: 'c7',
    icon: '🕒',
    text: 'You are enjoying yourself and carry on at half past ten at night.',
    answer: 'no',
    why: 'The window says 09:00–17:00. Those hours exist so somebody is there when something breaks, and testing outside them means an unexplained alarm at 2am and a very bad phone call.',
  },
  {
    id: 'c8',
    icon: '📁',
    text: 'On the staff wifi you find a shared folder of tutors’ scanned passports, wide open to the guest network.',
    answer: 'ask',
    why: 'This is exactly what the "stop and telephone" rule is for. It is in scope, it is serious, and it is somebody’s identity documents. Stop touching it and get a human on the phone now, not in the report next week.',
    trap: true,
  },
];

/* ------------------------------------------------------------------ */

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (v) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* private window */ }
};

export default function ScopeLab() {
  const saved = useMemo(load, []);

  const [picks, setPicks] = useState(saved.picks ?? {});
  const [openLetter, setOpenLetter] = useState(true);

  useEffect(() => { document.title = 'Read the scope · CyberCub Lab'; }, []);
  useEffect(() => { save({ picks }); }, [picks]);

  const right = CASES.filter((c) => picks[c.id] === c.answer).length;
  const allDone = right === CASES.length;

  useFinished('/lab/scope', allDone);
  const asking = CASES.find((c) => picks[c.id] !== c.answer) ?? null;

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>Read the scope</strong>
            <span className="lab-sub">Practical · Ethical Hacker, Module 1</span>
          </div>
        </div>
        <Link to="/learn/lesson/eh1l2-who-ethical-hackers-are" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <section className="lab-section">
        <div className="place-head">
          <div>
            <h2>Read the scope, then decide</h2>
            <p className="lab-lead">
              A real engagement starts with a letter like this one. Eight things happen during the
              week — for each, decide whether the letter allows it. The letter stays open beside you,
              because checking it is the skill.
            </p>
          </div>
          <span className="pill">{right} / {CASES.length}</span>
        </div>

        <Tutor
          avatar="cub-mint"
          say={
            allDone
              ? 'All eight. Notice what the hard ones had in common: none of them were about whether you could, and all of them were about whether you were allowed. That is the only line between this job and the other one.'
              : asking
                ? 'Before you answer, find the line in the letter that settles it. If you cannot find a line, that is itself the answer — ask.'
                : 'Read the letter first. All of it.'
          }
        />

        {/* ---------------- the letter ---------------- */}
        <div className="scope-letter">
          <button
            type="button"
            className="scope-toggle"
            onClick={() => setOpenLetter((o) => !o)}
            aria-expanded={openLetter}
          >
            📄 Authorisation to test — {LETTER.client}
            <span>{openLetter ? 'hide' : 'show'}</span>
          </button>

          {openLetter && (
            <div className="scope-body">
              <p className="scope-meta">
                Signed by <strong>{LETTER.signed}</strong> · {LETTER.dated}
              </p>

              <h3>Testing window</h3>
              <p className="scope-window">{LETTER.window}</p>

              <h3>In scope</h3>
              <ul className="scope-list is-in">
                {LETTER.inScope.map((s) => <li key={s}>{s}</li>)}
              </ul>

              <h3>Out of scope</h3>
              <ul className="scope-list is-out">
                {LETTER.outOfScope.map((s) => <li key={s}>{s}</li>)}
              </ul>

              <h3>Rules of engagement</h3>
              <ol className="scope-list is-rules">
                {LETTER.rules.map((s) => <li key={s}>{s}</li>)}
              </ol>

              <p className="scope-contact">☎️ Emergency contact: {LETTER.contact}</p>
            </div>
          )}
        </div>

        {/* ---------------- the decisions ---------------- */}
        {CASES.map((c) => {
          const pick = picks[c.id];
          const correct = pick === c.answer;
          return (
            <div key={c.id} className={`lab-case ${correct ? 'is-right' : pick ? 'is-wrong' : ''}`}>
              <span className="lab-case-n" aria-hidden="true">{c.icon}</span>
              <div className="grow">
                <p>{c.text}</p>
                <div className="lab-choices">
                  {CHOICES.map((ch) => (
                    <button
                      key={ch.id}
                      type="button"
                      className={`lab-choice ${pick === ch.id ? 'is-picked' : ''}`}
                      onClick={() => setPicks((prev) => ({ ...prev, [c.id]: ch.id }))}
                      disabled={correct}
                    >
                      {ch.icon} {ch.label}
                    </button>
                  ))}
                </div>
                {pick && (
                  <p className={`lab-feedback ${correct ? 'is-right' : 'is-wrong'}`}>
                    {correct
                      ? `✓ ${c.why}`
                      : c.trap
                        ? '✗ This is one of the ones that catches real people. Read the letter again — the answer is in it.'
                        : '✗ Not that. Which line of the letter covers this?'}
                  </p>
                )}
              </div>
            </div>
          );
        })}

        {allDone && (
          <div className="lab-done">
            <div>
              <strong>All eight.</strong>
              <p style={{ margin: '0.3rem 0 0' }}>
                Three of those had nothing to do with skill. A subdomain that was obviously theirs, a
                director saying go ahead, and a folder you should never have been able to open — and
                in every case the right move was slower and more boring than the interesting one.
                That is the job.
              </p>
            </div>
            <Link to="/learn/lesson/eh1l2-who-ethical-hackers-are" className="btn btn-sm">
              ← Back to the lesson
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
